import React, { useState, useEffect } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import Sidebar from "../components/Sidebar";
import { Bars3Icon } from "@heroicons/react/24/outline";
import SearchableDropdown from "../components/SearchableDropdown";

/* ── Product color palette — cycles through products ────── */
const PRODUCT_PALETTES = [
    {
        header: "bg-blue-600 text-white",
        sub1: "bg-blue-50 text-blue-800 border-b border-blue-200",
        sub2: "bg-blue-100 text-blue-900 border-b border-blue-200",
        target: "bg-yellow-50",
        achievement: "bg-orange-50",
        pct: "bg-sky-50",
        btd: "bg-red-50",
        profit: "bg-purple-50",
        cell: "bg-white",
        border: "border-blue-200",
        accent: "text-blue-700",
    },
    {
        header: "bg-emerald-600 text-white",
        sub1: "bg-emerald-50 text-emerald-800 border-b border-emerald-200",
        sub2: "bg-emerald-100 text-emerald-900 border-b border-emerald-200",
        target: "bg-yellow-50",
        achievement: "bg-orange-50",
        pct: "bg-teal-50",
        btd: "bg-red-50",
        profit: "bg-purple-50",
        cell: "bg-white",
        border: "border-emerald-200",
        accent: "text-emerald-700",
    },
    {
        header: "bg-violet-600 text-white",
        sub1: "bg-violet-50 text-violet-800 border-b border-violet-200",
        sub2: "bg-violet-100 text-violet-900 border-b border-violet-200",
        target: "bg-yellow-50",
        achievement: "bg-orange-50",
        pct: "bg-indigo-50",
        btd: "bg-red-50",
        profit: "bg-pink-50",
        cell: "bg-white",
        border: "border-violet-200",
        accent: "text-violet-700",
    },
    {
        header: "bg-rose-600 text-white",
        sub1: "bg-rose-50 text-rose-800 border-b border-rose-200",
        sub2: "bg-rose-100 text-rose-900 border-b border-rose-200",
        target: "bg-yellow-50",
        achievement: "bg-orange-50",
        pct: "bg-sky-50",
        btd: "bg-red-50",
        profit: "bg-purple-50",
        cell: "bg-white",
        border: "border-rose-200",
        accent: "text-rose-700",
    },
    {
        header: "bg-amber-600 text-white",
        sub1: "bg-amber-50 text-amber-800 border-b border-amber-200",
        sub2: "bg-amber-100 text-amber-900 border-b border-amber-200",
        target: "bg-yellow-50",
        achievement: "bg-orange-50",
        pct: "bg-lime-50",
        btd: "bg-red-50",
        profit: "bg-purple-50",
        cell: "bg-white",
        border: "border-amber-200",
        accent: "text-amber-700",
    },
];

const getPalette = (idx) => PRODUCT_PALETTES[idx % PRODUCT_PALETTES.length];

/* ── Stat Card ─────────────────────────────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value}</span>
    </div>
);

/* ═════════════════════════════════════════════════════════ */

const TargetAchievementReport = () => {
    const [employees, setEmployees] = useState([]);
    const [products, setProducts] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        employee_id: "",
        target_month: "",
        product_id: "",
        volume: "",
        rate: "",
        value_in_lakh: "",
    });

    useEffect(() => { fetchAllData(); }, []);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const empRes = await axios.get(`${SERVER_URL}/Rahul/get_employee.php`);
            setEmployees(Array.isArray(empRes.data) ? empRes.data : []);

            const prodRes = await axios.get(`${SERVER_URL}/Rahul/fetchProductAndCategory.php`);
            const productsData = Array.isArray(prodRes?.data?.products)
                ? prodRes.data.products
                : [];

            // only ACTIVE products
            const activeProducts = productsData.filter(
                (p) => (p.status || "").toUpperCase() === "ACTIVE"
            );

            setProducts(activeProducts);

            const reportRes = await axios.get(`${SERVER_URL}/Rahul/TaskHistory.php`);
            const targetHistory = reportRes?.data?.targetHistory || [];

            const grouped = {};
            targetHistory.forEach((item) => {
                const targetDate = new Date(`${item.target_month}-01`);
                const monthName = targetDate.toLocaleString("default", { month: "long" });
                const monthNumber = targetDate.getMonth() + 1;
                let qtr = [4, 5, 6].includes(monthNumber) ? "Q1" :
                    [7, 8, 9].includes(monthNumber) ? "Q2" :
                        [10, 11, 12].includes(monthNumber) ? "Q3" : "Q4";

                const rowKey = `${item.employee_id}_${item.target_month}`;
                if (!grouped[rowKey]) {
                    grouped[rowKey] = {
                        qtr, month: monthName,
                        employee_name: item.employee_name || "-",
                        designation: item.job_role || "-",
                        products_data: {},
                        totals: { tgt: 0, achv: 0, pct_achv: 0, profit: 0, growth: 0 },
                    };
                }
                const tgtVol = Number(item.volume || 0);
                const tgtRate = Number(item.rate || 0);
                const tgtVal = tgtVol * tgtRate;
                const achVol = Number(item.achieved_qty || 0);
                const achAvg = Number(item.avg_achieved_rate || 0);
                const achVal = Number(item.achieved_amount || 0);
                const pctVol = tgtVol > 0 ? ((achVol / tgtVol) * 100).toFixed(2) : 0;
                const pctVal = tgtVal > 0 ? ((achVal / tgtVal) * 100).toFixed(2) : 0;
                const btdVol = tgtVol - achVol;
                const btdVal = tgtVal - achVal;

                grouped[rowKey].products_data[item.product_id] = {
                    tgt_vol: tgtVol, tgt_rate: tgtRate, tgt_val: tgtVal,
                    ach_vol: achVol, ach_avg: achAvg, ach_val: achVal,
                    pct_vol: pctVol, pct_val: pctVal,
                    btd_vol: btdVol, btd_val: btdVal,
                    profit: 0,
                };
                grouped[rowKey].totals.tgt += tgtVal;
                grouped[rowKey].totals.achv += achVal;
            });

            Object.values(grouped).forEach((row) => {
                row.totals.pct_achv = row.totals.tgt > 0
                    ? ((row.totals.achv / row.totals.tgt) * 100).toFixed(2) : 0;
            });
            setReportData(Object.values(grouped));
        } catch (error) {
            console.log("FETCH ERROR:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const volume = Number(formData.volume || 0);
        const rate = Number(formData.rate || 0);
        setFormData((prev) => ({ ...prev, value_in_lakh: (volume * rate).toFixed(2) }));
    }, [formData.volume, formData.rate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const employeeData = JSON.parse(localStorage.getItem("employee"));
            const payload = { ...formData, assigned_by: employeeData?.employee_id || "" };
            const response = await axios.post(`${SERVER_URL}/Rahul/insertNewTasks.php`, payload);
            const result = response.data;
            if (result?.status === "success") {
                alert(result?.message || "Target Assigned Successfully");
                setIsModalOpen(false);
                setFormData({ employee_id: "", target_month: "", product_id: "", volume: "", rate: "", value_in_lakh: "" });
                fetchAllData();
            } else {
                alert(result?.message || "Failed To Assign Target");
            }
        } catch (error) {
            alert(error?.response?.data?.message || "Server Error");
        }
    };

    /* ── Summary stats ─────────────────────────────────── */
    const totalTarget = reportData.reduce((s, r) => s + (r.totals.tgt || 0), 0);
    const totalAchievement = reportData.reduce((s, r) => s + (r.totals.achv || 0), 0);
    const overallPct = totalTarget > 0 ? ((totalAchievement / totalTarget) * 100).toFixed(1) : 0;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: "#f0f4f8" }}>
                <div className="text-slate-500 text-sm animate-pulse">Loading report data…</div>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}
                    onLogout={() => { localStorage.clear(); }} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <div>
                                    <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                        Target vs Achievement
                                    </h1>
                                    <p className="text-[11px] text-slate-400 m-0">Employee wise product target report</p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="h-10 px-5 rounded-lg text-sm font-semibold text-white transition-all"
                            style={{
                                background: "linear-gradient(90deg, #1e40af, #0369a1)",
                                boxShadow: "0 1px 4px rgba(15,23,42,0.15)"
                            }}
                        >
                            + Assign Target
                        </button>
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 flex flex-col gap-4 p-5">

                    {/* ── STATS BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex flex-wrap gap-2 items-center">
                            <StatCard label="Total Target" value={`₹${totalTarget.toLocaleString()}`}
                                colorClass="bg-yellow-50 text-yellow-700 border border-yellow-200"
                                dotClass="bg-yellow-400" />
                            <StatCard label="Achievement" value={`₹${totalAchievement.toLocaleString()}`}
                                colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200"
                                dotClass="bg-emerald-500" />
                            <StatCard label="Overall %" value={`${overallPct}%`}
                                colorClass="bg-blue-50 text-blue-700 border border-blue-200"
                                dotClass="bg-blue-500" />
                            <StatCard label="Employees" value={reportData.length}
                                colorClass="bg-violet-50 text-violet-700 border border-violet-200"
                                dotClass="bg-violet-500" />
                            {/* Legend */}
                            <div className="flex items-center gap-3 ml-auto flex-wrap">
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-yellow-700">
                                    <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300 inline-block" /> Target
                                </span>
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-orange-700">
                                    <span className="w-3 h-3 rounded bg-orange-100 border border-orange-300 inline-block" /> Achievement
                                </span>
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-sky-700">
                                    <span className="w-3 h-3 rounded bg-sky-100 border border-sky-300 inline-block" /> % Achv
                                </span>
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-red-700">
                                    <span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block" /> BTD
                                </span>
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-purple-700">
                                    <span className="w-3 h-3 rounded bg-purple-100 border border-purple-300 inline-block" /> Profit
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── TABLE ── */}
                    <div className="rounded-xl bg-white border border-slate-200 overflow-hidden"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>

                        {/* Outer scroll wrapper — horizontal scroll for the whole table */}
                        <div className="overflow-x-auto overflow-y-auto max-h-[600px]"
                            style={{ position: "relative" }}>
                            <table className="text-xs border-collapse"
                                style={{ tableLayout: "fixed", minWidth: `${4 * 120 + products.length * 11 * 70 + 5 * 80}px` }}>

                                {/* ── COLGROUP for fixed widths ── */}
                                <colgroup>
                                    <col style={{ width: "70px" }} />  {/* Qtr */}
                                    <col style={{ width: "90px" }} />  {/* Month */}
                                    <col style={{ width: "180px" }} /> {/* Employee */}
                                    <col style={{ width: "130px" }} /> {/* Designation */}
                                    {products.map((p) => (
                                        <React.Fragment key={p.id}>
                                            {/* 11 cols per product */}
                                            {Array(11).fill(0).map((_, ci) => (
                                                <col key={ci} style={{ width: "70px" }} />
                                            ))}
                                        </React.Fragment>
                                    ))}
                                    {/* 5 total cols */}
                                    {Array(5).fill(0).map((_, ci) => (
                                        <col key={`tot-${ci}`} style={{ width: "80px" }} />
                                    ))}
                                </colgroup>

                                <thead className="sticky top-0 z-20">

                                    {/* ── LEVEL 1 ── */}
                                    <tr>
                                        {/* FIXED COLS — Qtr, Month, Employee, Designation */}
                                        {[
                                            { label: "Qtr", w: "70px" },
                                            { label: "Month", w: "90px" },
                                            { label: "Employee Name", w: "180px" },
                                            { label: "Designation", w: "130px" },
                                        ].map(({ label, w }, idx) => (
                                            <th key={label} rowSpan={3}
                                                className="border border-slate-200 p-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100"
                                                style={{ position: "sticky", left: [0, 70, 160, 340][idx], zIndex: 30, width: w, minWidth: w }}>
                                                {label}
                                            </th>
                                        ))}

                                        {/* PRODUCT HEADERS */}
                                        {products.map((product, pi) => {
                                            const pal = getPalette(pi);
                                            return (
                                                <th key={product.id} colSpan={11}
                                                    className={`border border-slate-300 p-2 text-center text-xs font-bold tracking-wide ${pal.header}`}>
                                                    {product.name}
                                                </th>
                                            );
                                        })}

                                        {/* TOTALS */}
                                        <th colSpan={5}
                                            className="border border-slate-300 p-2 text-center text-xs font-bold bg-slate-700 text-white tracking-wide">
                                            TOTALS
                                        </th>
                                    </tr>

                                    {/* ── LEVEL 2 ── */}
                                    <tr>
                                        {products.map((product, pi) => {
                                            const pal = getPalette(pi);
                                            return (
                                                <React.Fragment key={product.id}>
                                                    <th colSpan={3} className={`border border-slate-200 p-1.5 text-center text-[10px] font-bold uppercase tracking-wider ${pal.target}`}>
                                                        🎯 Target
                                                    </th>
                                                    <th colSpan={3} className={`border border-slate-200 p-1.5 text-center text-[10px] font-bold uppercase tracking-wider ${pal.achievement}`}>
                                                        ✅ Achievement
                                                    </th>
                                                    <th colSpan={2} className={`border border-slate-200 p-1.5 text-center text-[10px] font-bold uppercase tracking-wider ${pal.pct}`}>
                                                        % Achv
                                                    </th>
                                                    <th colSpan={2} className={`border border-slate-200 p-1.5 text-center text-[10px] font-bold uppercase tracking-wider ${pal.btd}`}>
                                                        BTD
                                                    </th>
                                                    <th className={`border border-slate-200 p-1.5 text-center text-[10px] font-bold uppercase tracking-wider ${pal.profit}`}>
                                                        Profit
                                                    </th>
                                                </React.Fragment>
                                            );
                                        })}
                                        {/* Totals L2 */}
                                        <th className="border border-slate-200 p-1.5 text-center text-[10px] font-semibold bg-yellow-50 text-yellow-800">Tgt</th>
                                        <th className="border border-slate-200 p-1.5 text-center text-[10px] font-semibold bg-orange-50 text-orange-800">Achv</th>
                                        <th className="border border-slate-200 p-1.5 text-center text-[10px] font-semibold bg-sky-50 text-sky-800">%</th>
                                        <th className="border border-slate-200 p-1.5 text-center text-[10px] font-semibold bg-purple-50 text-purple-800">Profit</th>
                                        <th className="border border-slate-200 p-1.5 text-center text-[10px] font-semibold bg-green-50 text-green-800">Growth</th>
                                    </tr>

                                    {/* ── LEVEL 3 ── */}
                                    <tr>
                                        {products.map((product, pi) => {
                                            const pal = getPalette(pi);
                                            return (
                                                <React.Fragment key={product.id}>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.target}`}>Vol</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.target}`}>Rate</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.target}`}>Value</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.achievement}`}>Vol</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.achievement}`}>Avg</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.achievement}`}>Value</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.pct}`}>Vol%</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.pct}`}>Val%</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.btd}`}>Vol</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.btd}`}>Val</th>
                                                    <th className={`border border-slate-200 p-1 text-center text-[10px] font-semibold ${pal.profit}`}>₹</th>
                                                </React.Fragment>
                                            );
                                        })}
                                        <th className="border border-slate-200 p-1 text-center text-[10px] font-semibold bg-yellow-50 text-yellow-700">Tgt</th>
                                        <th className="border border-slate-200 p-1 text-center text-[10px] font-semibold bg-orange-50 text-orange-700">Achv</th>
                                        <th className="border border-slate-200 p-1 text-center text-[10px] font-semibold bg-sky-50 text-sky-700">%</th>
                                        <th className="border border-slate-200 p-1 text-center text-[10px] font-semibold bg-purple-50 text-purple-700">Profit</th>
                                        <th className="border border-slate-200 p-1 text-center text-[10px] font-semibold bg-green-50 text-green-700">Growth</th>
                                    </tr>
                                </thead>

                                {/* ── BODY ── */}
                                <tbody>
                                    {reportData.length > 0 ? (
                                        reportData.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                {/* FIXED: Qtr */}
                                                <td className="border border-slate-100 p-2 text-center font-bold text-slate-600 bg-white"
                                                    style={{ position: "sticky", left: 0, zIndex: 10, minWidth: "70px" }}>
                                                    <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[10px] font-bold">
                                                        {row.qtr}
                                                    </span>
                                                </td>
                                                {/* FIXED: Month */}
                                                <td className="border border-slate-100 p-2 text-center text-slate-600 bg-white"
                                                    style={{ position: "sticky", left: 70, zIndex: 10, minWidth: "90px" }}>
                                                    {row.month}
                                                </td>
                                                {/* FIXED: Employee */}
                                                <td className="border border-slate-100 p-2 font-semibold text-slate-800 bg-white"
                                                    style={{ position: "sticky", left: 160, zIndex: 10, minWidth: "180px" }}>
                                                    {row.employee_name}
                                                </td>
                                                {/* FIXED: Designation */}
                                                <td className="border border-slate-100 p-2 text-slate-500 bg-white"
                                                    style={{ position: "sticky", left: 340, zIndex: 10, minWidth: "130px", boxShadow: "2px 0 6px rgba(0,0,0,0.07)" }}>
                                                    {row.designation}
                                                </td>

                                                {/* PRODUCT DATA */}
                                                {products.map((prod, pi) => {
                                                    const data = row?.products_data?.[prod.id] || {};
                                                    const pal = getPalette(pi);
                                                    const pctVolNum = parseFloat(data.pct_vol || 0);
                                                    const pctValNum = parseFloat(data.pct_val || 0);

                                                    return (
                                                        <React.Fragment key={prod.id}>
                                                            {/* TARGET */}
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.target}`}>{data.tgt_vol || 0}</td>
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.target}`}>{data.tgt_rate || 0}</td>
                                                            <td className={`border border-slate-100 p-1.5 text-right font-semibold ${pal.target} ${pal.accent}`}>{data.tgt_val || 0}</td>
                                                            {/* ACHIEVEMENT */}
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.achievement}`}>{data.ach_vol || 0}</td>
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.achievement}`}>{data.ach_avg || 0}</td>
                                                            <td className={`border border-slate-100 p-1.5 text-right font-semibold ${pal.achievement} text-emerald-700`}>{data.ach_val || 0}</td>
                                                            {/* % ACHV */}
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.pct}`}>
                                                                <span className={`text-xs font-bold ${pctVolNum >= 100 ? "text-emerald-600" : pctVolNum >= 50 ? "text-amber-600" : "text-red-500"}`}>
                                                                    {data.pct_vol || 0}%
                                                                </span>
                                                            </td>
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.pct}`}>
                                                                <span className={`text-xs font-bold ${pctValNum >= 100 ? "text-emerald-600" : pctValNum >= 50 ? "text-amber-600" : "text-red-500"}`}>
                                                                    {data.pct_val || 0}%
                                                                </span>
                                                            </td>
                                                            {/* BTD */}
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.btd} text-red-600`}>{data.btd_vol || 0}</td>
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.btd} text-red-600 font-semibold`}>{data.btd_val || 0}</td>
                                                            {/* PROFIT */}
                                                            <td className={`border border-slate-100 p-1.5 text-right ${pal.profit} text-purple-700 font-semibold`}>{data.profit || 0}</td>
                                                        </React.Fragment>
                                                    );
                                                })}

                                                {/* TOTALS */}
                                                <td className="border border-slate-100 p-1.5 text-right font-bold bg-yellow-50 text-yellow-800">{row?.totals?.tgt || 0}</td>
                                                <td className="border border-slate-100 p-1.5 text-right bg-orange-50 text-emerald-700 font-semibold">{row?.totals?.achv || 0}</td>
                                                <td className="border border-slate-100 p-1.5 text-right bg-sky-50">
                                                    <span className={`text-xs font-bold ${parseFloat(row?.totals?.pct_achv || 0) >= 100 ? "text-emerald-600" : parseFloat(row?.totals?.pct_achv || 0) >= 50 ? "text-amber-600" : "text-red-500"}`}>
                                                        {row?.totals?.pct_achv || 0}%
                                                    </span>
                                                </td>
                                                <td className="border border-slate-100 p-1.5 text-right bg-purple-50 text-purple-700">{row?.totals?.profit || 0}</td>
                                                <td className="border border-slate-100 p-1.5 text-right bg-green-50 text-green-700">{row?.totals?.growth || 0}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={products.length * 11 + 9}
                                                className="text-center p-10 text-slate-400">
                                                No Report Data Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MODAL ── */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-slate-200"
                        style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>

                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100"
                            style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}>
                            <h2 className="text-base font-bold text-white">Assign Set Target</h2>
                            <button onClick={() => setIsModalOpen(false)}
                                className="text-white/70 hover:text-white text-lg leading-none">✕</button>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Employee */}
                            <div>
                                <SearchableDropdown
                                    label="Employee"
                                    name="employee_id"
                                    value={formData.employee_id}
                                    onChange={(val) =>
                                        setFormData((prev) => ({ ...prev, employee_id: val }))
                                    }
                                    options={employees.map((emp) => ({
                                        label: `${emp.name} (${emp.id})`,
                                        value: emp.id,
                                    }))}
                                />
                            </div>

                            {/* Month */}
                            <div>
                                <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wider">Select Month</label>
                                <input type="month" name="target_month" value={formData.target_month} onChange={handleInputChange}
                                    className="w-full border border-slate-200 bg-slate-50 p-2.5 rounded-lg text-sm text-slate-700" required />
                            </div>

                            {/* Product */}
                            <div>
                                <SearchableDropdown
                                    label="Product"
                                    name="product_id"
                                    value={formData.product_id}
                                    onChange={(val) =>
                                        setFormData((prev) => ({ ...prev, product_id: val }))
                                    }
                                    options={products.map((prod) => ({
                                        label: prod.name,
                                        value: prod.id,
                                    }))}
                                />
                            </div>

                            {/* Volume & Rate */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wider">Volume</label>
                                    <input type="number" name="volume" value={formData.volume} onChange={handleInputChange}
                                        className="w-full border border-slate-200 bg-slate-50 p-2.5 rounded-lg text-sm text-slate-700" />
                                </div>
                                <div>
                                    <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wider">Rate</label>
                                    <input type="number" name="rate" value={formData.rate} onChange={handleInputChange}
                                        className="w-full border border-slate-200 bg-slate-50 p-2.5 rounded-lg text-sm text-slate-700" />
                                </div>
                            </div>

                            {/* Value */}
                            <div>
                                <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wider">Computed Value</label>
                                <input type="text" value={formData.value_in_lakh} disabled
                                    className="w-full border border-slate-100 p-2.5 rounded-lg bg-slate-100 text-sm text-slate-500 font-semibold" />
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setIsModalOpen(false)}
                                    className="flex-1 border border-slate-200 bg-slate-50 hover:bg-slate-100 py-2.5 rounded-lg text-sm text-slate-600 font-medium transition-colors">
                                    Cancel
                                </button>
                                <button onClick={handleSubmit}
                                    className="flex-1 text-white py-2.5 rounded-lg text-sm font-semibold transition-all"
                                    style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}>
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TargetAchievementReport;